import React from 'react';
import { ArrowLeft, Check, Globe2, Laptop, Moon, Palette, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '../../contexts/ThemeContext';

type LanguageCode = 'fr' | 'en' | 'es' | 'ar' | 'it';

interface LanguageOption {
  code: LanguageCode;
  label: string;
  nativeLabel: string;
  flag: string;
}

const LANGUAGES: LanguageOption[] = [
  { code: 'fr', label: 'Français', nativeLabel: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'Anglais', nativeLabel: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Espagnol', nativeLabel: 'Español', flag: '🇪🇸' },
  { code: 'ar', label: 'Arabe', nativeLabel: 'العربية', flag: '🇸🇦' },
  { code: 'it', label: 'Italien', nativeLabel: 'Italiano', flag: '🇮🇹' },
];

type ThemeChoice = 'light' | 'dark';

const SoverdiPreferencesR7: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0] as LanguageCode;

  const selectLanguage = async (language: LanguageCode) => {
    if (language !== currentLanguage) {
      // The canonical i18next detector persists this in localStorage (`i18nextLng`).
      // LanguageProvider then updates document lang/dir, including Arabic RTL.
      await i18n.changeLanguage(language);
    }
  };

  const selectTheme = (requestedTheme: ThemeChoice) => {
    if (requestedTheme !== theme) toggleTheme();
  };

  return (
    <div className="space-y-5 pb-24 sm:space-y-6 lg:pb-6" data-testid="preferences-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-settings')}
            className="inline-flex min-h-[40px] items-center gap-2 text-sm font-medium text-[var(--color-primary)]"
            data-testid="preferences-back"
          >
            <ArrowLeft className="h-4 w-4" /> Paramètres
          </button>
          <div className="mt-2 flex items-center gap-2 text-[var(--color-primary)]">
            <Palette className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">Interface</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">Préférences d’affichage</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Choisissez la langue et l’apparence de l’interface Agri Safe sur cet appareil.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          <Laptop className="h-3.5 w-3.5" /> Enregistré sur cet appareil
        </span>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-4 sm:px-5">
          <div className="flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="font-semibold text-[var(--text-primary)]">Langue</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Le choix est conservé dans ce navigateur par le mécanisme de langue Agri Safe. Les éléments disposant d’une traduction suivent cette langue.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-5">
          {LANGUAGES.map((language) => {
            const selected = language.code === currentLanguage;
            return (
              <button
                key={language.code}
                type="button"
                onClick={() => void selectLanguage(language.code)}
                aria-pressed={selected}
                data-testid={`preferences-language-${language.code}`}
                className={`relative min-h-[92px] rounded-xl border p-4 text-left transition-colors ${
                  selected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                    : 'border-[var(--border-light)] hover:bg-[var(--gray-50)]'
                }`}
              >
                {selected && (
                  <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-primary)] text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                )}
                <span className="text-xl" aria-hidden="true">{language.flag}</span>
                <p className="mt-2 font-semibold text-[var(--text-primary)]">{language.nativeLabel}</p>
                {language.nativeLabel !== language.label && (
                  <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">{language.label}</p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-4 sm:px-5">
          <h2 className="font-semibold text-[var(--text-primary)]">Apparence</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Le thème est appliqué immédiatement et conservé dans ce navigateur.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5">
          {([
            { value: 'light' as const, title: 'Clair', description: 'Interface lumineuse', icon: Sun },
            { value: 'dark' as const, title: 'Sombre', description: 'Interface sombre', icon: Moon },
          ]).map(({ value, title, description, icon: Icon }) => {
            const selected = theme === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => selectTheme(value)}
                aria-pressed={selected}
                data-testid={`preferences-theme-${value}`}
                className={`flex min-h-[84px] items-center gap-3 rounded-xl border p-4 text-left transition-colors ${
                  selected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                    : 'border-[var(--border-light)] hover:bg-[var(--gray-50)]'
                }`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--gray-100)]">
                  <Icon className="h-5 w-5 text-[var(--text-primary)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[var(--text-primary)]">{title}</p>
                  <p className="mt-0.5 text-sm text-[var(--text-secondary)]">{description}</p>
                </div>
                {selected && (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Laptop className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-tertiary)]" />
          <div>
            <p className="font-semibold text-[var(--text-primary)]">Préférences locales</p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Ces choix ne sont pas encore synchronisés avec votre compte Agri Safe ni avec vos autres appareils. Aucun réglage technique de l’exploitation n’est modifié ici.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoverdiPreferencesR7;
